import React, { useEffect, useLayoutEffect } from "react";

const CURRENT_PRODUCT_NAME = "English Job Interview Simulator";
const CURRENT_LOGO = "/brand/globe-logo.svg";
const LEGACY_PRODUCT_PATTERNS = [
  /My English Coach™ Interview Simulator/g,
  /My English Coach Interview Simulator/g,
];

function canonicalizeBrandText(value) {
  if (typeof value !== "string" || !value) return value;
  return LEGACY_PRODUCT_PATTERNS.reduce(
    (text, pattern) => text.replace(pattern, CURRENT_PRODUCT_NAME),
    value,
  );
}

function normalizeLegacyBrand(root) {
  if (typeof document === "undefined" || !root) return 0;
  let changes = 0;

  const normalizeTextNode = (node) => {
    if (!node || node.nodeType !== Node.TEXT_NODE) return;
    const next = canonicalizeBrandText(node.nodeValue || "");
    if (next !== node.nodeValue) {
      node.nodeValue = next;
      changes += 1;
    }
  };

  if (root.nodeType === Node.TEXT_NODE) {
    normalizeTextNode(root);
  } else if (root.nodeType === Node.ELEMENT_NODE || root.nodeType === Node.DOCUMENT_NODE) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();
    while (textNode) {
      normalizeTextNode(textNode);
      textNode = walker.nextNode();
    }
  }

  const elements = [];
  if (root.nodeType === Node.ELEMENT_NODE) elements.push(root);
  if (root.querySelectorAll) elements.push(...root.querySelectorAll("img, [alt], [title], [aria-label]"));

  for (const element of elements) {
    if (element.tagName === "IMG") {
      const src = element.getAttribute("src") || "";
      if (src.endsWith("/brand/logo.png") || src.endsWith("/brand/logo.original.png")) {
        element.setAttribute("src", CURRENT_LOGO);
        changes += 1;
      }
    }

    for (const attr of ["alt", "title", "aria-label"]) {
      if (!element.hasAttribute?.(attr)) continue;
      const current = element.getAttribute(attr) || "";
      const next = canonicalizeBrandText(current);
      if (next !== current) {
        element.setAttribute(attr, next);
        changes += 1;
      }
    }
  }

  if (root === document || root === document.documentElement || root === document.body) {
    const nextTitle = canonicalizeBrandText(document.title);
    if (nextTitle !== document.title) {
      document.title = nextTitle;
      changes += 1;
    }
  }

  return changes;
}

function previewUrl(url) {
  if (url == null || typeof window === "undefined") return url;
  try {
    const parsed = new URL(String(url), window.location.href);
    if (parsed.origin !== window.location.origin) return url;
    parsed.searchParams.set("qaPreview", "1");
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

export default function EnvironmentBanner() {
  const isStaging = process.env.REACT_APP_ENVIRONMENT === "staging";

  // Migration safety net: several older screens pre-date the Language Masters
  // product identity. Normalize any legacy product name/logo before paint and
  // keep watching dynamic routes/modals so retired branding cannot reappear.
  // This is intentionally environment-independent so production receives the
  // same protection after staging is promoted.
  useLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;

    const initialChanges = normalizeLegacyBrand(document);
    if (isStaging && initialChanges > 0) {
      console.warn(`[Brand audit] normalized ${initialChanges} legacy branding occurrence(s).`);
    }

    const observer = new MutationObserver((mutations) => {
      let changes = 0;
      for (const mutation of mutations) {
        if (mutation.type === "characterData") {
          changes += normalizeLegacyBrand(mutation.target);
          continue;
        }
        if (mutation.type === "attributes") {
          changes += normalizeLegacyBrand(mutation.target);
          continue;
        }
        for (const node of mutation.addedNodes) {
          changes += normalizeLegacyBrand(node);
        }
      }
      if (isStaging && changes > 0) {
        console.warn(`[Brand audit] normalized ${changes} legacy branding occurrence(s).`);
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["src", "alt", "title", "aria-label"],
    });

    return () => observer.disconnect();
  }, [isStaging]);

  // Responsive Preview Lab starts the real staging app with ?qaPreview=1.
  // Once active, keep that flag on every same-origin SPA navigation, redirect,
  // and ordinary link so Setup -> Prepare -> Interview -> Report stays in QA
  // preview mode. AppRoutes uses the flag to suppress destructive browser-exit
  // cleanup while the admin is inspecting or resizing a test session.
  useEffect(() => {
    if (!isStaging || typeof window === "undefined") return undefined;
    const previewActive = new URLSearchParams(window.location.search).get("qaPreview") === "1";
    if (!previewActive) return undefined;

    const history = window.history;
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function pushState(state, title, url) {
      return originalPushState.call(this, state, title, previewUrl(url));
    };
    history.replaceState = function replaceState(state, title, url) {
      return originalReplaceState.call(this, state, title, previewUrl(url));
    };

    const preservePreviewOnLink = (event) => {
      const anchor = event.target?.closest?.("a[href]");
      if (!anchor || anchor.hasAttribute("download")) return;
      if (anchor.target && anchor.target.toLowerCase() !== "_self") return;

      try {
        const parsed = new URL(anchor.href, window.location.href);
        if (parsed.origin !== window.location.origin) return;
        parsed.searchParams.set("qaPreview", "1");
        anchor.setAttribute("href", `${parsed.pathname}${parsed.search}${parsed.hash}`);
      } catch {
        // Leave malformed or non-standard links untouched.
      }
    };

    document.addEventListener("click", preservePreviewOnLink, true);

    return () => {
      document.removeEventListener("click", preservePreviewOnLink, true);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [isStaging]);

  if (!isStaging) return null;

  return (
    <div
      className="w-full bg-amber-100 border-b border-amber-300 text-amber-950 text-center text-[11px] font-semibold uppercase tracking-[0.18em] py-1.5"
      data-testid="staging-environment-banner"
      role="status"
    >
      Staging Environment · Test Data Only · Not Production
    </div>
  );
}
